import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { loginSchema, type LoginValues } from "@/lib/schemas";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import AuthShell from "@/layouts/AuthShell";
import { Button, Input, Label, toast } from "@/components/ds";

// Internal ResFil staff have no customer org — route them to the internal portal.
export async function postLoginRedirect(): Promise<string> {
  try {
    const me = await api.me();
    const role = (me as any).user?.platform_role;
    if (!me.organization && role === "sales_rep") return "/internal/sales/me";
    if (!me.organization && (role === "platform_admin" || role === "onboarding_staff")) return "/internal";
  } catch { /* fall through */ }
  return "/app/dashboard";
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (values: LoginValues) => {
    setError("");
    try {
      const res = await login(values.email, values.password);
      if (res?.mfa_required && res.mfa_challenge) {
        navigate("/mfa", { state: { challenge: res.mfa_challenge, email: values.email } });
        return;
      }
      toast.success("Signed in");
      navigate(await postLoginRedirect());
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    }
  };

  return (
    <AuthShell title="Sign in to your dashboard" subtitle="Welcome back.">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="email-input" {...register("email")} />
          {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" data-testid="password-input" {...register("password")} />
          {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
        </div>
        {error && <p className="text-sm font-medium text-red-600" data-testid="login-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="login-submit">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </Button>
      </form>
      <div className="mt-4 flex items-center justify-start text-sm">
        <Link to="/forgot-password" className="text-blue-600 hover:text-blue-700" data-testid="forgot-link">Forgot password?</Link>
      </div>
    </AuthShell>
  );
}
